import {DepartmentDataServicesModalProps} from "./DepartmentDataServicesModal";
import {useCallback, useMemo, useRef, useState} from "react";
import {
  DepartmentDataServiceFormValues,
  DepartmentDataServiceUpdateEvent,
  DepartmentDataServiceUpdateFn
} from "../../../forms/DepartmentDataServiceForm/DepartmentDataServiceForm.types";
import {ServiceCatalogEntry} from "../../../../models/DepartmentData/ServiceCatalogEntry";
import {DepartmentDataService} from "../../../../models/DepartmentData/DepartmentDataService";

export type UseDepartmentDataServicesModalResponse = {
  configuredServices: DepartmentDataServiceUpdateEvent[];
  servicesList: ServiceCatalogEntry[];
  isLoadingServices: boolean;
  serviceLoadError: boolean;
  getSavedFormValuesForRow: (formId: string) => Partial<DepartmentDataServiceFormValues> | undefined;
  getSavedServiceForRow: (formId: string) => DepartmentDataService | undefined;
  resetConfiguredServices: () => void;
  handleServiceUpdate: DepartmentDataServiceUpdateFn;
  handleServiceDelete: (formId: string) => void;
  addService: () => void;
  loadServices: () => Promise<void>;
};

const createFormIdGenerator = () => {
  const usedIds: Set<string> = new Set();
  return () => {
    let newId: string = `service-form-${Math.random().toString(36).substr(2, 9)}`;
    while(usedIds.has(newId)) {
      newId = `service-form-${Math.random().toString(36).substr(2, 9)}`;
    }
    usedIds.add(newId);
    return newId;
  };
};

export function useDepartmentDataServicesModal(props: Pick<DepartmentDataServicesModalProps, 'store' | 'initialServices'>): UseDepartmentDataServicesModalResponse {
  const { store, initialServices: savedServices } = props;
  const [configuredServices, setConfiguredServices] = useState<DepartmentDataServiceUpdateEvent[]>([]);
  const generateFormId = useMemo(() => createFormIdGenerator(), []);
  const [servicesList, setServicesList] = useState<ServiceCatalogEntry[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState<boolean>(false);
  const [serviceLoadError, setServiceLoadError] = useState<boolean>(false);
  const savedFormValuesByRowId = useRef<Record<string, Partial<DepartmentDataServiceFormValues>>>({});
  const savedServicesByRowId = useRef<Record<string, DepartmentDataService>>({});

  const getSavedFormValuesForRow = useCallback((formId: string): Partial<DepartmentDataServiceFormValues> | undefined => {
    return savedFormValuesByRowId.current[formId];
  }, []);

  const getSavedServiceForRow = useCallback((formId: string): DepartmentDataService | undefined => {
    return savedServicesByRowId.current[formId];
  }, []);

  const resetConfiguredServices = useCallback((): void => {
    const savedFormValuesByRowIdForReset: Record<string, Partial<DepartmentDataServiceFormValues>> = {};
    const savedServicesByRowIdForReset: Record<string, DepartmentDataService> = {};
    const configuredRows: DepartmentDataServiceUpdateEvent[] = savedServices?.map(
      (service: DepartmentDataService): DepartmentDataServiceUpdateEvent => {
        const formId: string = generateFormId();
        savedServicesByRowIdForReset[formId] = { ...service };
        savedFormValuesByRowIdForReset[formId] = {
          service_id: service.service_id,
          amount: service.amount,
        };

        return {
          ...service,
          valid: true,
          formId,
        };
      }
    ) ?? [];

    savedFormValuesByRowId.current = savedFormValuesByRowIdForReset;
    savedServicesByRowId.current = savedServicesByRowIdForReset;
    setConfiguredServices(configuredRows);
  }, [generateFormId, savedServices]);

  const handleServiceUpdate: DepartmentDataServiceUpdateFn = useCallback((update: DepartmentDataServiceUpdateEvent): void => {
    setConfiguredServices((prevServices: DepartmentDataServiceUpdateEvent[]) => {
      const existingIndex = prevServices.findIndex((service: DepartmentDataServiceUpdateEvent) => service.formId === update.formId);
      if (existingIndex !== -1) {
        const updatedServices: DepartmentDataServiceUpdateEvent[] = [...prevServices];
        updatedServices[existingIndex] = { ...updatedServices[existingIndex], ...update };
        return updatedServices;
      } else {
        return [...prevServices, update];
      }
    });
  }, []);

  const handleServiceDelete = useCallback((formId: string): void => {
    setConfiguredServices((prevServices: DepartmentDataServiceUpdateEvent[]) => prevServices.filter((service: DepartmentDataServiceUpdateEvent) => service.formId !== formId));
  }, []);

  const addService = useCallback((): void => {
    setConfiguredServices((prevServices: DepartmentDataServiceUpdateEvent[]) => [...prevServices, {
      valid: false,
      formId: `${generateFormId()}`,
    }]);
  }, [generateFormId]);

  const loadServices = useCallback(async (): Promise<void> => {
    setIsLoadingServices(true);
    setServiceLoadError(false);

    try {
      const services: ServiceCatalogEntry[] = await store.loadServices();
      setServicesList(services);
    } catch {
      setServiceLoadError(true);
    } finally {
      setIsLoadingServices(false);
    }
  }, [store]);

  return {
    configuredServices,
    servicesList,
    isLoadingServices,
    serviceLoadError,
    getSavedFormValuesForRow,
    getSavedServiceForRow,
    resetConfiguredServices,
    handleServiceUpdate,
    handleServiceDelete,
    addService,
    loadServices,
  };
}